import { useCallback, useContext, useEffect, useState } from "react";
import { saveAs } from "file-saver";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CiExport } from "react-icons/ci";
import { FaLock, FaLockOpen } from "react-icons/fa";
import { TiUserDeleteOutline } from "react-icons/ti";

import { DarkModeContext } from "../../Context/DarkModeContext.jsx";
import axiosInstance from "../../Context/axiosInstance.js";

const EXPORT_SHEETS = [
  { key: "includeCourses", label: "Purchased courses" },
  { key: "includeTransactions", label: "Transactions" },
  { key: "includeSessionReservations", label: "Session reservations" },
  { key: "includeSessionRequests", label: "Session requests" },
];

const DEFAULT_EXPORT_SHEETS = Object.fromEntries(
  EXPORT_SHEETS.map(({ key }) => [key, false]),
);

const getExportFileName = (contentDisposition = "") => {
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)?.[1];
  const plain = /filename="?([^";]+)"?/i.exec(contentDisposition)?.[1];

  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }

  return plain || "students.xlsx";
};

const getExportErrorMessage = async (error) => {
  const status = error.response?.status;

  if (status === 401) return "Your session expired. Please sign in again.";
  if (status === 403) return "You do not have permission to export students.";

  let data = error.response?.data;
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text());
    } catch {
      data = null;
    }
  }

  if (data?.detail) return data.detail;
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors.map(({ reason }) => reason).filter(Boolean).join(" ");
  }

  return error.message || "The students export could not be downloaded.";
};

export default function UserList() {
  const { isDarkMode } = useContext(DarkModeContext);
  const navigate = useNavigate();

  // Retrieve values for URL parameters
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const pageFromUrl = parseInt(params.get("page"), 10) || 1;
  const pageSizeFromUrl = parseInt(params.get("pageSize"), 10) || 20;
  const searchQueryFromUrl = params.get("searchQuery") || "";
  const sortColumnFromUrl = params.get("sortColumn") || "RegistrationDate";
  const sortDirectionFromUrl = params.get("sortDirection") || "desc";

  const [page, setPage] = useState(pageFromUrl);
  const [pageSize, setPageSize] = useState(pageSizeFromUrl);
  const [searchQuery, setSearchQuery] = useState(searchQueryFromUrl);
  const [sortColumn, setSortColumn] = useState(sortColumnFromUrl);
  const [sortDirection, setSortDirection] = useState(sortDirectionFromUrl);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportSheets, setExportSheets] = useState(DEFAULT_EXPORT_SHEETS);
  const [totalPages, setTotalPages] = useState(1);

  //Locking and Unlocking a student
  const [selectedUsers, setSelectedUsers] = useState([]);

  //Delete a student
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    setPage(pageFromUrl);
    setPageSize(pageSizeFromUrl);
  }, [pageFromUrl, pageSizeFromUrl]);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axiosInstance.get(
        `/users?page=${page}&pageSize=${pageSize}&sortColumn=${sortColumn}&sortDirection=${sortDirection}&search=${encodeURIComponent(
          searchQuery,
        )}`,
      );

      setUsers(response.data.response);
      setTotalPages(Math.ceil(response.data.totalCount / pageSize));
    } catch (error) {
      if (error.response && error.response.status === 401) {
        // Handle unauthorized error
      } else {
        toast.error(error.message || "An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, sortColumn, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      navigate(`?page=${newPage}&pageSize=${pageSize}`);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
    navigate(`?page=1&pageSize=${newSize}`); // Update URL with new page size
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((id) => id !== userId)
        : [...prevSelected, userId],
    );
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setExportProgress(null);
      const exportParams = Object.fromEntries(
        Object.entries({
          search: searchQuery.trim() || undefined,
          ...exportSheets,
        }).filter(([, value]) => value !== "" && value != null && value !== false),
      );
      const response = await axiosInstance.get("/users/export", {
        params: exportParams,
        responseType: "blob",
        onDownloadProgress: ({ loaded, total }) => {
          setExportProgress(
            total ? Math.min(100, Math.round((loaded / total) * 100)) : null,
          );
        },
      });
      const fileName = getExportFileName(response.headers["content-disposition"]);

      saveAs(response.data, fileName);
      setShowExportOptions(false);
      toast.success("Students export downloaded.");
    } catch (error) {
      toast.error(await getExportErrorMessage(error));
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const handleDeleteUsers = async () => {
    try {
      await Promise.all(
        selectedUsers.map((userId) => axiosInstance.delete(`/users/${userId}`)),
      );
      setSelectedUsers([]);
      toast.success(
        `${selectedUsers.length > 1 ? "Users" : "User"} Deleted successfully!`,
      );
      setTimeout(async () => {
        await fetchData();
      }, 3000);
    } catch (error) {
      toast.error("Deleting users failed. Please try again.");
    }
  };
  const handleLockUsers = async () => {
    try {
      await Promise.all(
        selectedUsers.map((userId) =>
          axiosInstance.post(`/users/${userId}/lock`),
        ),
      );
      setSelectedUsers([]);
      toast.success(
        `${selectedUsers.length > 1 ? "Users" : "User"} locked successfully!`,
      );
      setTimeout(async () => {
        await fetchData(); // Call fetchData to refetch courses
      }, 3000);
    } catch (error) {
      toast.error("Failed to lock users. Please try again.");
    }
  };

  const handleUnlockUsers = async () => {
    try {
      await Promise.all(
        selectedUsers.map((userId) =>
          axiosInstance.post(`/users/${userId}/unlock`),
        ),
      );
      setSelectedUsers([]);
      toast.success(
        `${selectedUsers.length > 1 ? "Users" : "User"} unlocked successfully!`,
      );
      setTimeout(async () => {
        await fetchData(); // Call fetchData to refetch courses
      }, 3000);
    } catch (error) {
      toast.error("Failed to unlock users. Please try again.");
    }
  };

  const handleUserInfoPage = (userId, name) => {
    if (userId) {
      const queryParams = new URLSearchParams({
        page,
        pageSize,
        searchQuery, // Use state variable
        sortColumn, // Use state variable
        sortDirection, // Use state variable
      }).toString();
      navigate(`/students/user/${userId}?${queryParams}`, { state: { name } });
    } else {
      console.error("User ID is undefined");
    }
  };

  // Toggle sort direction
  const toggleSortDirection = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  return (
    <div
      className={`flex flex-col lg:flex-row lg:items-start ${
        isDarkMode ? "bg-[#292929] text-black" : "bg-white text-black"
      } p-4 lg:p-8 min-h-screen`}
    >
      <div
        className={`${
          isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"
        } p-4 lg:p-8 mt-[40px] lg:mt-0 rounded-lg shadow-xl flex-1`}
      >
        <div className="flex flex-col lg:flex-row lg:justify-between mb-4">
          <div className="flex flex-col lg:flex-row lg:justify-start pb-2">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`${
                isDarkMode ? "bg-[#292929]" : "bg-gray-100"
              } px-4 py-2 border border-gray-300 rounded-lg`}
            />
          </div>
          <div className="flex flex-col lg:flex-row lg:gap-2">
            <div className="relative mb-2 lg:mb-0">
              <button
                disabled={exporting}
                onClick={() => setShowExportOptions((current) => !current)}
                aria-expanded={showExportOptions}
                className={`flex w-full items-center justify-center rounded-lg bg-blue-500 px-4 py-2 text-white lg:px-6 lg:py-3 ${
                  exporting
                    ? "cursor-not-allowed opacity-60"
                    : "transition-transform transform hover:scale-customScale"
                }`}
              >
                <CiExport size={24} className="pr-2" />
                Export Excel
              </button>

              <AnimatePresence>
                {showExportOptions && (
                  <>
                    <motion.button
                      type="button"
                      aria-label="Close export options"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setShowExportOptions(false)}
                      className="fixed inset-0 z-20 cursor-default bg-black/20 backdrop-blur-[1px]"
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.97 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      className={`absolute right-0 z-30 mt-2 w-72 origin-top-right rounded-lg border p-4 shadow-xl ${
                        isDarkMode
                          ? "border-gray-600 bg-[#292929] text-white"
                          : "border-gray-200 bg-white text-gray-900"
                      }`}
                    >
                    <p className="mb-1 font-semibold">Export students</p>
                    <p className="mb-3 text-sm text-gray-500">
                      The Students sheet is always included. Select any extra
                      sheets.
                    </p>
                    <div className="space-y-2">
                      {EXPORT_SHEETS.map(({ key, label }) => (
                        <label
                          key={key}
                          className="flex cursor-pointer items-center gap-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={exportSheets[key]}
                            disabled={exporting}
                            onChange={(event) =>
                              setExportSheets((current) => ({
                                ...current,
                                [key]: event.target.checked,
                              }))
                            }
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                    {exporting && (
                      <div className="mt-4" aria-live="polite">
                        <div className="mb-1 flex justify-between text-xs text-gray-500">
                          <span>Preparing workbook...</span>
                          {exportProgress !== null && (
                            <span>{exportProgress}%</span>
                          )}
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                          {exportProgress !== null ? (
                            <motion.div
                              className="h-full rounded-full bg-blue-500"
                              initial={{ width: 0 }}
                              animate={{ width: `${exportProgress}%` }}
                              transition={{ duration: 0.2 }}
                            />
                          ) : (
                            <motion.div
                              className="h-full w-1/3 rounded-full bg-blue-500"
                              animate={{ x: ["-100%", "300%"] }}
                              transition={{
                                duration: 1.1,
                                ease: "easeInOut",
                                repeat: Infinity,
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      disabled={exporting}
                      onClick={handleExport}
                      className="mt-4 flex w-full items-center justify-center rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {exporting ? (
                        <>
                          <svg
                            className="h-5 w-5 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            <circle cx="12" cy="12" r="10" strokeWidth="4" />
                            <path
                              d="M4 12a8 8 0 018-8v8H4z"
                              fill="currentColor"
                            />
                          </svg>
                          <span className="ml-2">Exporting...</span>
                        </>
                      ) : (
                        "Download workbook"
                      )}
                    </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
            <button
              disabled={selectedUsers.length === 0}
              onClick={() => setShowDeleteConfirm(true)}
              className={`flex items-center mb-2 lg:mb-0 px-4 py-2 lg:px-6 lg:py-3 justify-center bg-red-500 text-white rounded-lg transition-opacity ${
                selectedUsers.length === 0
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-red-600"
              }`}
            >
              <TiUserDeleteOutline size={25} className="pr-2" />
              Delete
            </button>

            <button
              disabled={selectedUsers.length === 0}
              onClick={handleLockUsers}
              className={`flex items-center mb-2 lg:mb-0 px-4 py-2 lg:px-6 lg:py-3 justify-center bg-red-500 text-white rounded-lg transition-opacity ${
                selectedUsers.length === 0
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-red-600"
              }`}
            >
              <FaLock size={20} className="pr-2" />
              Lock
            </button>
            <button
              disabled={selectedUsers.length === 0}
              onClick={handleUnlockUsers}
              className={`flex items-center mb-2 lg:mb-0 px-4 py-2 lg:px-6 lg:py-3 justify-center bg-green-500 text-white rounded-lg transition-opacity ${
                selectedUsers.length === 0
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-green-600"
              }`}
            >
              <FaLockOpen size={20} className="pr-2" />
              Unlock
            </button>
          </div>
        </div>
        <div className="w-full overflow-x-auto pb-4">
          <div className="w-full overflow-x-auto custom-scrollbar pb-4">
            {loading ? (
              <div className="flex justify-center items-center h-[640px]">
                <div className="loader">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            ) : (
              <table className="w-full table-auto divide-y divide-gray-200">
                <thead
                  className={`${
                    isDarkMode
                      ? "bg-[#1e1e1e] text-white"
                      : "bg-white text-black"
                  }`}
                >
                  <tr>
                    <th className="px-2 md:px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          setSelectedUsers(
                            e.target.checked
                              ? users.map((user) => user.id)
                              : [],
                          )
                        }
                        checked={selectedUsers.length === users.length}
                      />
                    </th>
                    <th
                      onClick={() => toggleSortDirection("Name")}
                      className="cursor-pointer px-2 md:px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider whitespace-normal"
                    >
                      Name
                      {sortColumn === "Name" && (
                        <span className="text-sm px-1 items-center text-blue-400">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => toggleSortDirection("GradeLevel")}
                      className="cursor-pointer px-2 md:px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider whitespace-normal"
                    >
                      Grade Level
                      {sortColumn === "GradeLevel" && (
                        <span className="text-sm px-1 items-center text-blue-400">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>

                    <th
                      onClick={() => toggleSortDirection("PhoneNumber")}
                      className="cursor-pointer px-2 md:px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider whitespace-normal hidden sm:table-cell"
                    >
                      Phone Number
                      {sortColumn === "PhoneNumber" && (
                        <span className="text-sm px-1 items-center text-blue-400">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => toggleSortDirection("RegistrationDate")}
                      className="cursor-pointer px-2 md:px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider whitespace-normal hidden sm:table-cell"
                    >
                      Registration Date
                      {sortColumn === "RegistrationDate" && (
                        <span className="text-sm px-1 items-center text-blue-400">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => toggleSortDirection("Email")}
                      className="cursor-pointer px-2 md:px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider whitespace-normal"
                    >
                      Email
                      {sortColumn === "Email" && (
                        <span className="text-sm px-1 items-center text-blue-400">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th className="px-2 md:px-4 py-2 text-center text-base font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${
                    isDarkMode ? "text-white" : "text-black"
                  } divide-y divide-gray-200`}
                >
                  {users.length > 0 &&
                    users.map((user) => (
                      <tr
                        key={user.id}
                        className={`animate-zoomIn transition-colors duration-200 motion-reduce:transition-none ${
                          isDarkMode
                            ? "hover:bg-blue-400/10 focus-within:bg-blue-400/10"
                            : "hover:bg-blue-50 focus-within:bg-blue-50"
                        } ${
                          user.status === "Locked" && "text-red-500"
                        }`}
                      >
                        <td
                          className={`px-2 md:px-4 py-2 whitespace-normal font-medium ${user}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => handleSelectUser(user.id)}
                          />
                        </td>
                        <td
                          onClick={() => handleUserInfoPage(user.id)}
                          className="cursor-pointer px-2 md:px-4 py-2 whitespace-normal text-base text-center break-words max-w-[220px] md:max-w-[320px]"
                        >
                          <h2 className="text-[16px] truncate">{user.name}</h2>
                          <p className="text-[12px] truncate">{user.id}</p>
                        </td>
                        <td
                          onClick={() => handleUserInfoPage(user.id)}
                          className="cursor-pointer px-2 md:px-4 py-2 whitespace-normal text-base text-center"
                        >
                          {user.gradeLevel}
                        </td>
                        <td
                          onClick={() => handleUserInfoPage(user.id)}
                          className="cursor-pointer px-2 md:px-4 py-2 whitespace-normal text-base text-center hidden sm:table-cell"
                        >
                          {user.phoneNumber}
                        </td>
                        <td
                          onClick={() => handleUserInfoPage(user.id)}
                          className="cursor-pointer px-2 md:px-4 py-2 whitespace-normal text-base text-center hidden sm:table-cell"
                        >
                          {new Date(user.registrationDate).toLocaleString(
                            "en-EG",
                            {
                              timeZone: "Africa/Cairo",
                            },
                          )}
                        </td>
                        <td
                          onClick={() => handleUserInfoPage(user.id)}
                          className="cursor-pointer px-2 md:px-4 py-2 whitespace-normal text-base text-center break-words max-w-[220px] truncate"
                        >
                          {user.email}
                        </td>
                        <td
                          onClick={() => handleUserInfoPage(user.id)}
                          className={`flex justify-center items-center h-16 ${
                            user.status === "Active" && "text-green-600"
                          } cursor-pointer px-4 py-2 whitespace-nowrap text-base`}
                        >
                          {user.status === "Locked" ? (
                            <FaLock size={20} className="text-red-600" />
                          ) : (
                            <FaLockOpen size={20} className="text-green-600" />
                          )}
                        </td>
                      </tr>
                    ))}
                  {!users.length && !loading && (
                    <tr className="h-[650px]">
                      <td
                        colSpan="7"
                        className={`${
                          isDarkMode ? "text-white" : "text-gray-500"
                        } text-center text-lg text-gray-500`}
                      >
                        No Students found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end py-4 text-black">
          <label
            htmlFor="pageSize"
            className={`${
              isDarkMode ? "text-white" : "text-gray-700"
            } mr-2 text-sm`}
          >
            Students per page:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            {[20, 30, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            className={`px-4 py-2 bg-gray-300 text-black rounded-lg ${
              page <= 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-400"
            }`}
          >
            Previous
          </button>
          <span className={"text-lg mt-2 sm:mt-0"}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            className={`px-4 py-2 bg-gray-300 text-black rounded-lg ${
              page >= totalPages
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-400"
            }`}
          >
            Next
          </button>
        </div>
      </div>
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/40 animate-fadeIn p-4 overflow-y-auto">
          <div
            className={`relative w-[90%] max-w-md rounded-2xl shadow-2xl p-6 border  max-h-[calc(100dvh-2rem)] overflow-y-auto ${
              isDarkMode
                ? "bg-[#1e1e1e] border-gray-700 text-white"
                : "bg-white border-gray-200 text-gray-800"
            } animate-scaleIn  `}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div
                className={`flex items-center justify-center w-14 h-14 rounded-full ${
                  isDarkMode ? "bg-red-900/30" : "bg-red-100"
                }`}
              >
                <TiUserDeleteOutline
                  size={32}
                  className="text-red-500 drop-shadow-md"
                />
              </div>

              <h2 className="text-xl font-semibold">Confirm Deletion</h2>
              <p className="text-sm leading-relaxed opacity-80">
                Are you sure you want to delete{" "}
                <span className="font-semibold">{selectedUsers.length}</span>{" "}
                {selectedUsers.length > 1 ? "users" : "user"}?
                <br />
                This action cannot be undone.
              </p>

              <div className="flex justify-center gap-4 mt-4 w-full">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className={`w-1/2 py-2.5 rounded-xl font-medium border transition-all duration-200 ${
                    isDarkMode
                      ? "border-gray-600 hover:bg-gray-800"
                      : "border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setShowDeleteConfirm(false);
                    await handleDeleteUsers();
                  }}
                  className="w-1/2 py-2.5 rounded-xl font-medium bg-red-600 hover:bg-red-700 text-white transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
